import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Loader2, Sparkles } from "lucide-react";
import { API } from "../../config/api";

const toIsoDate = (d) => d.toISOString().slice(0, 10);
const todayMinusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toIsoDate(d);
};

export const ReportFilterDialog = ({ open, onClose, template, initialFilters, onGenerate, busy }) => {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [windowDays, setWindowDays] = useState("30");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState(toIsoDate(new Date()));
  const [sourceBasis, setSourceBasis] = useState("latest");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects`);
        setProjects(r.data?.projects || r.data || []);
      } catch { setProjects([]); }
    })();
  }, [open]);

  useEffect(() => {
    if (!open || !template) return;
    const defaultDays = template.default_date_window_days || 30;
    setWindowDays(String(defaultDays));
    setDateFrom(todayMinusDays(defaultDays));
    setProjectId(initialFilters?.project_id || "");
    setSourceBasis(initialFilters?.source_basis || "latest");
    if (initialFilters?.date_from) setDateFrom(initialFilters.date_from.slice(0, 10));
    if (initialFilters?.date_to) setDateTo(initialFilters.date_to.slice(0, 10));
  }, [open, template, initialFilters]);

  const onPresetChange = (val) => {
    setWindowDays(val);
    if (val === "custom") return;
    const n = parseInt(val, 10);
    setDateFrom(todayMinusDays(n));
    setDateTo(toIsoDate(new Date()));
  };

  const submit = () => {
    const filters = {
      project_id: projectId || undefined,
      date_from: dateFrom ? `${dateFrom}T00:00:00+00:00` : undefined,
      date_to: dateTo ? `${dateTo}T23:59:59+00:00` : undefined,
      source_basis: template?.supports?.source_basis ? sourceBasis : undefined,
    };
    onGenerate?.(filters);
  };

  if (!template) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="report-filter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-500" />
            {template.name}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{template.description}</p>
        <div className="space-y-4 mt-2">
          {template.supports?.project && (
            <div>
              <Label className="text-xs">Project</Label>
              <Select value={projectId || "__all"} onValueChange={(v) => setProjectId(v === "__all" ? "" : v)}>
                <SelectTrigger data-testid="filter-project" className="mt-1.5"><SelectValue placeholder="All projects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">All Projects</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id || p.name} value={p.name}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {template.supports?.date_range && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Window</Label>
                <Select value={windowDays} onValueChange={onPresetChange}>
                  <SelectTrigger data-testid="filter-window" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Today</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="180">Last 6 months</SelectItem>
                    <SelectItem value="365">Last 12 months</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input data-testid="filter-date-from" type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setWindowDays("custom"); }} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input data-testid="filter-date-to" type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setWindowDays("custom"); }} className="mt-1.5" />
              </div>
            </div>
          )}
          {template.supports?.source_basis && (
            <div>
              <Label className="text-xs">Source Attribution</Label>
              <Select value={sourceBasis} onValueChange={setSourceBasis}>
                <SelectTrigger data-testid="filter-source-basis" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest touch (re-inquiry source wins)</SelectItem>
                  <SelectItem value="first">First touch (original source)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onClose?.()} disabled={busy}>Cancel</Button>
          <Button
            data-testid="generate-report-btn"
            onClick={submit}
            disabled={busy}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {busy ? "Generating…" : "Generate Report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportFilterDialog;
